import { useTableData } from '@customer/shared/hooks/useTableData';
import { useAppDispatch, useAppSelector } from '@customer/store/hooks';
import { ColDef } from 'ag-grid-community';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  InvestigationAssetTrackBasicModel,
  InvestigationBasicModel,
  InvestigationsQueryParams,
  useGetInvestigationsQuery,
} from '@customer/store/apis/investigationsApi';
import { useNavigate } from 'react-router-dom';
import InvestigationStatusRenderer from '@customer/components/tables/cells/investigation-status/InvestigationStatusRenderer';
import InvestigationTypeRenderer from '@customer/components/tables/cells/investigation-type/InvestigationTypeCellRenderer';
import useFilters from '@customer/filters/useFilters';
import { FilterGroup } from '@customer/filters/types';
import { useAbsoluteTimePartialColDef } from '@customer/components/tables/cells/absolute-time/useAbsoluteTime';
import { openSidePanel, SidePanel } from '@customer/store/slices/globalSlice';
import { ModelName } from '@customer/store/types';

const getAssetsList = (assets: InvestigationAssetTrackBasicModel[]): string => {
  return assets.map((asset: InvestigationAssetTrackBasicModel): string => asset.n).join(', ');
};

export const useInvestigations = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const siteId = useAppSelector(state => state.auth.siteId);
  const filters = useFilters<InvestigationsQueryParams>(FilterGroup.Investigations);

  const { data, error, ...requestRemainder } = useGetInvestigationsQuery(filters, {
    refetchOnMountOrArgChange: 60,
    skip: typeof siteId === 'undefined',
  });

  const { items: investigations, countAll, countFilter } = data ?? {};

  const absoluteTimeColDef = useAbsoluteTimePartialColDef<InvestigationBasicModel>();

  const columnDefs = useMemo<ColDef<InvestigationBasicModel>[]>(() => {
    return [
      {
        field: 'ts',
        headerName: t('Date and Time'),
        maxWidth: 200,
        sort: 'desc',
        ...absoluteTimeColDef,
      },
      {
        field: 'it',
        headerName: t('InvestigationType'),
        cellRenderer: InvestigationTypeRenderer,
        maxWidth: 200,
      },
      {
        field: 'name',
        headerName: t('Field.InvestigationName.Label'),
      },
      {
        field: 'assets',
        headerName: t('Assets'),
        valueFormatter: params => getAssetsList(params.value),
        getQuickFilterText: params => getAssetsList(params.data.assets),
      },
      {
        field: 'createdBy',
        headerName: t('CreatedBy'),
        valueFormatter: params => params.value?.dn,
        getQuickFilterText: params => params.data.createdBy?.dn,
        maxWidth: 300,
      },
      {
        field: 'st',
        headerName: t('Status'),
        cellRenderer: InvestigationStatusRenderer,
        maxWidth: 200,
      },
    ];
  }, [t, absoluteTimeColDef]);

  const noResultsDescription = t('Collection.NoItems', { modelName: ModelName.Investigation });

  const { hasData, noDataDescription } = useTableData<InvestigationBasicModel>({
    data: investigations,
    ...requestRemainder,
    noResultsDescription,
  });

  const onRowClick = useCallback(
    (data: InvestigationBasicModel) => {
      navigate(`/investigation/${data.id}`);
    },
    [navigate],
  );

  const onCreate = useCallback((): void => {
    dispatch(
      openSidePanel({
        sidePanel: SidePanel.CreateCustomInvestigation,
      }),
    );
  }, [dispatch]);

  return {
    columnDefs,
    investigations,
    error,
    countAll,
    countFilter,
    hasData,
    noDataDescription,
    noResultsDescription,
    onRowClick,
    onCreate,
  };
};

export default useInvestigations;
